import React, { useEffect, useState } from 'react'
import Footer from '../../../layouts/Footer'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import axios from '../../../lib/axios'
import { Label } from '../../../components/ui/label'
import { Combobox } from '../../../components/ui/combobox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table'
import { cn, convertToUrl } from '../../../lib/utils'
import { Bike, ChevronRight, Flag } from 'lucide-react'
import * as Icon from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'
import { eventsURL } from '../../../lib/fetch'
import useAuth from '../../../hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { getTranslationString } from '../../../lib/translation'
import { AxiosError } from 'axios'
import { Badge } from '../../../components/ui/badge'
import Top3 from '../../../assets/icons/top-3.png'
import Top5 from '../../../assets/icons/top-5.png'

type PerfectPredictionBadgeType = 'top3' | 'top5' | 'default';

interface PerfectPredictionBadge {
  type: PerfectPredictionBadgeType;
  count: number;
}

const getPerfectPredictionBadgeTypes = (extraInfo?: string): PerfectPredictionBadge[] => {
  const normalizedExtraInfo = (extraInfo || '').toUpperCase();
  const top3Count = normalizedExtraInfo.split('BONUS_TOP_3').length - 1;
  const top5Count = normalizedExtraInfo.split('BONUS_TOP_5').length - 1;
  const badgeTypes: PerfectPredictionBadge[] = [];

  if (top3Count > 0) {
    badgeTypes.push({ type: 'top3', count: top3Count });
  }
  if (top5Count > 0) {
    badgeTypes.push({ type: 'top5', count: top5Count });
  }
  if (badgeTypes.length === 0) {
    badgeTypes.push({ type: 'default', count: 1 });
  }
  return badgeTypes;
}

const Department = () => {
    const {auth} = useAuth()
    const {t, i18n} = useTranslation()
    const {partnername, departmentname} = useParams()
    const encodedPartnerName = partnername ? encodeURIComponent(partnername) : ""
    const encodedDepartmentName = departmentname ? encodeURIComponent(departmentname) : ""
    const navigate = useNavigate()

    const [selected_competition, setselected_competition] = useState<string>();
    const [competition_results, setcompetition_results] = useState<EventRanking[] | null>(null);

    const { data:partner } = useQuery<Partner | null>({
        queryKey: [`partner_${partnername}`],
        queryFn: async () => {
        try {
            const {data} = await axios.get(`partner/${encodedPartnerName}`)
            if(Array.isArray(data)){throw new Error()}
            if(data){ return data }
            else { throw new Error()}
        } catch (error) {
            navigate('/')
            return null
        }
        }
    })

    const { data:events } = useQuery<PartnerEvent[]>({
        queryKey: [`events_${partnername}`],
        queryFn: async () => {
        try {
            const {data} = await axios.get(`partners/${partner.m_iPartnerID}/${eventsURL()}`)
            let filteredData = data.filter(i=>i.m_bActive===true)
            if(Array.isArray(filteredData)){ setselected_competition(filteredData[0].m_iEventID.toString()); return filteredData }
            else { throw new Error()}
        } catch (error) {
            console.log(error)
            return null
        }
        },
        enabled: !!partner
    })

    const { data:pronos } = useQuery<Prono[] | null>({
      queryKey: [`pronos_${selected_competition}`],
      queryFn: async () => {
        try {
          if(!selected_competition){ return null }
          const { data } = await axios.get(`pronos?eventid=${selected_competition}`)
          if(!Array.isArray(data)){ throw new Error('No array') }
          return data
        } catch (error) {
          console.log(error)
          return null
        }
      },
      enabled: !!selected_competition
    })

    const { data:department } = useQuery<PartnerDepartment | null>({
      queryKey: [`department_${departmentname}`],
      queryFn: async () => {
        try {
          const {data} = await axios.get(`partner/${encodedPartnerName}/${encodedDepartmentName}`)
          console.log(data)
          if(Array.isArray(data)){throw new Error('multiple departments found')}
          return data
        } catch (error) {
          console.log(error)
          navigate(`/${partnername}`)
          return null;
        }
      },
      enabled: !!partner
    })

    const language = i18n.language.toUpperCase() as Language;
    const pronoOptions = pronos
      ?.filter(prono => prono.m_bClosed)
      .map(prono => ({
        value: prono.m_iPronoID,
        label: getTranslationString(language, prono, 'm_sDescriptionNL') || prono.m_sDescriptionNL
      })) || [];

    console.log(pronoOptions)
    useEffect(()=>{
      if(selected_competition && department){
        getEventRankings()
      }
    }, [selected_competition, department])

    const getEventRankings = async () => {
      try {
        //const {data} = await axios.get(`partners/${partner.m_iPartnerID}/events/${selected_competition}/ranking&departmentid=${department?.m_iDepartmentID}`)
        
        const {data} = await axios.get(`ranking?eventid=${selected_competition}&departmentid=${department?.m_iDepartmentID}`)
        if(!Array.isArray(data)){throw new Error('no ranking array')}
        setcompetition_results(data)
      } catch (error) {
        if(error instanceof AxiosError){ 
          if(error.response.status === 400){
            if(error.response.data.toLowerCase().includes('partner id')){
              toast.error(`${t("error.400_ranking_get")}`, {duration:5000}); return;
            }
            if(error.response.data.toLowerCase().includes('event id')){
              toast.error(`${t("error.400_ranking_get-2")}`, {duration:5000}); return;
            }
          }
          if(error.response.status === 401){
            toast.error(`${t("error.401_ranking_get")}`, {duration:5000}); return;
          }
          if(error.response.status === 404){
            // toast.error(`${t("error.404_ranking_get")}`, {duration:5000}); return;
          }
        }

        // toast.error(t("partner.o_department.no_ranking"), {id:`ranking_${selected_competition}`})
      }
    }

    function returnTranslationObject(translations:Translation[]) {
      return translations.find(i=>i.m_sLangCode===i18n.language.toUpperCase())
    }

  return (
    <>
    <Toaster />
    <div className='relative flex flex-col items-center pt-32 pb-16 bg-tifo'>
      <div className='relative z-20 w-full max-w-5xl mx-4 text-white'>
        <div className='mt-[20vh] flex-col flex items-start gap-10 mx-4 lg:mx-0 pb-16'>
          <div className="w-auto h-16 p-2 rounded-sm min-w-16 bg-white/50">{partner && partner.m_sLogo ? <img className='object-cover w-auto h-full' src={partner.m_sLogo} alt="Logo"/> : <></>}</div>
          <div>
            <h1 className='mt-4 text-3xl font-bold sm:text-6xl'>{department?.m_aTranslations ? (returnTranslationObject(department.m_aTranslations)?.m_sNameTransl || department?.m_sName) : department?.m_sName}</h1>
            <p className='max-w-3xl mt-6 text-sm font-medium leading-loose sm:text-lg sm:mt-16'>
              {department?.m_aTranslations && (returnTranslationObject(department.m_aTranslations)?.m_sDescTransl)}
            </p>
          </div>
        </div>        
      </div>

      <div className='absolute top-0 left-0 w-full h-full'>
        <div className='relative top-0 left-0 z-10 w-full h-full bg-gradient-to-b from-tifo/20 to-tifo'></div>
        <div className='absolute top-0 left-0 w-full h-full'><img className='object-cover w-full h-full' alt="department image" src={department && department.m_sImageUrl ? department.m_sImageUrl : "https://images.pexels.com/photos/248547/pexels-photo-248547.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"}/></div>

      </div>

    </div>

    <div className='absolute top-0 left-0 w-full h-fit'>
        <div className='relative h-full'>
          <div className='w-full h-full absolute top-0 left-0 bg-gradient-to-b from-tifo/20 to-tifo z-[1]'></div>
        </div>
    </div>

    <div className='flex justify-center py-16 bg-slate-50'>
      <div className='w-full max-w-5xl mx-4'>
        <h2 className='max-w-4xl text-4xl font-bold leading-tight'>{t("partner.o_department.ranking")}</h2>

        <div className='flex flex-col gap-8 p-8 my-8 bg-white rounded-lg shadow-lg'>
          <div className='flex flex-col gap-1 w-fit'>
              <Label className='ml-2 font-semibold uppercase text-neutral-600' htmlFor='competition'>{t("partner.o_department.competition")}</Label>
              <Combobox selectedValue={selected_competition} onValueChange={(v)=>{setselected_competition(v.value.toString())}} options={events ? events.map((v)=>({label: getTranslationString(i18n.language.toUpperCase() as Language,v.m_oEvent, "m_sNameNL") || v.m_oEvent.m_sNameNL, value:v.m_iEventID})) : []} className='min-w-[250px] rounded-full' placeholder={t("partner.o_department.competition_placeholder")} input_placeholder={t("partner.o_department.search_placeholder")} id='competition'/>
          </div>

          {(selected_competition && competition_results) &&
            <div className='overflow-auto border rounded-md'>
                <Table className=''>
                    <TableHeader className='bg-slate-200'>
                        <TableRow className='bg-slate-200'>
                          <TableHead className='w-8'></TableHead>
                          <TableHead className='py-4'>{t("partner.o_department.position")}</TableHead>
                          <TableHead className='py-4'>{t("partner.o_department.name")}</TableHead>
                          <TableHead className='py-4'>{t("partner.o_department.points")}</TableHead>
                          <TableHead className='w-8'></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {competition_results.map((rank,i)=>{
                            return (
                              <TableRow className={`${rank.m_iChangePositionDepartment===0&&'font-semibold'} ${rank.m_iUserID === auth.data.m_iUserID && 'text-green-600 font-semibold'} bg-tifo-darker hover:bg-tifo-light/10`} key={i}>
                                <TableCell>
                                  {rank.m_iChangePositionPartner !== 0 
                                  ? 
                                  <div className='relative flex flex-col items-center justify-center'>
                                      {pronoOptions.length > 1 && rank.m_iChangePositionPartner > 0  ? <Icon.ArrowUp className='absolute top-0 w-3 h-3 -translate-x-1/2 -translate-y-full text-emerald-500 left-1/2' /> : null}
                                      {pronoOptions.length > 1 && rank.m_iChangePositionPartner < 0 ? <Icon.ArrowDown className='absolute bottom-0 w-3 h-3 text-red-500 -translate-x-1/2 translate-y-full left-1/2' /> : null}
                                      {pronoOptions.length > 1 && rank.m_iChangePositionPartner === 0 ? <Icon.Equal className='absolute top-0 w-3 h-3 -translate-x-1/2 -translate-y-full text-cyan-200 left-1/2'/> : null}
                                      <p 
                                          className={cn(
                                              pronoOptions.length > 1 && rank.m_iChangePositionPartner > 0 
                                              ? 'text-emerald-500' : 
                                                  pronoOptions.length > 1 && rank.m_iChangePositionPartner  < 0 
                                                  ? 'text-red-500'
                                                  : 'text-neutral-400'
                                          )}
                                      >
                                          {rank.m_iChangePositionPartner}
                                      </p>
                                  </div>
                                  : null
                                  }
                                </TableCell>
                                <TableCell className='py-6'>{i+1}</TableCell>
                                <TableCell className='flex items-center gap-1 py-6'>
                                  {rank.m_iUserID === auth.data.m_iUserID && <ChevronRight className='w-3'/>} {rank.m_sUser} {rank.m_iUserID === auth.data.m_iUserID && ` (${t("partner.o_department.you")})`}
                                  {(rank.m_iPerfectPrediction ?? 0) > 0 && (
                                      <div className="flex items-center gap-1 shrink-0">
                                        {getPerfectPredictionBadgeTypes(rank.m_sExtraInfo).map((badge, idx) => (
                                          <Badge
                                              key={`${rank.m_iUserID}-${badge.type}-${idx}`}
                                              variant="secondary"
                                              className="bg-tifo-light text-tifo text-xs px-2 py-0.5 flex items-center gap-1 rounded-full whitespace-nowrap shrink-0"
                                              title={String(t("ranking.perfect_prediction"))}
                                          >
                                              {badge.type === 'top3' && <img src={Top3} className='w-4 h-4' alt='top 3 bonus' />}
                                              {badge.type === 'top5' && <img src={Top5} className='w-4 h-4' alt='top 5 bonus' />}
                                              {badge.type === 'default' && <Icon.BadgeCheck className="w-4 h-4" />}
                                              {badge.count}
                                          </Badge>
                                        ))}
                                      </div>
                                  )}
                                </TableCell>
                                <TableCell className='py-6'>{rank.m_iPoints}</TableCell>
                                <TableCell>
                                  {rank.m_iChangePoints > 0
                                    ? <div className="relative flex flex-col items-center justify-center gap-0 text-emerald-500">
                                        <Icon.ArrowUp className='absolute top-0 w-3 h-3 -translate-x-1/2 -translate-y-full left-1/2' />
                                        <p className='text-xs'>+{rank.m_iChangePoints}</p>
                                    </div>
                                    : null
                                  }
                                </TableCell>
                              </TableRow>
                            )
                        })}              
                    </TableBody>
                </Table>
            </div>
          }
        </div>

        

      </div>
    </div>

    <div className='flex justify-center w-full pt-16 pb-32 bg-white'>
        <div className='w-full max-w-5xl mx-4'>
          <h2 className='text-4xl font-bold w-fit'>{t("partner.o_department.events")}</h2>

          <div className='grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4 mt-10 '>
            {events && events.map((event, index) => {
              return (
                <Link to={`/${partnername}/${getTranslationString(i18n.language.toUpperCase() as Language, event.m_oEvent, "m_sUrlNL") || event.m_oEvent.m_sUrlNL}`} key={index}>
                <div className='flex flex-col justify-between h-full overflow-hidden duration-200 rounded-lg shadow-lg w-fit hover:scale-105 hover:shadow-xl'>
                  <div className='w-min-content h-full min-h-[150px] max-h-[250px]'>
                    <img className={`${event.m_oEvent.m_bClosed && 'grayscale'} w-full h-full max-h-[250px] min-h-[150px] object-cover`} src={event.m_oEvent.m_sImageUrl || "https://img.aso.fr/core_app/img-cycling-prx-jpg/header-arrivee/4871/0:0,2000:690-2000-0-60/84728"} alt="" />
                  </div>
                  <div className='flex flex-col gap-2 p-4 bg-white'>
                    <h4 className={`${event.m_oEvent.m_bClosed ? 'text-black/70' : 'text-green-700'} text-lg font-bold`}>{getTranslationString(i18n.language.toUpperCase() as Language, event.m_oEvent, "m_sNameNL") || event.m_oEvent.m_sNameNL}</h4>
                    <div className='relative'>
                      <p className='max-h-[50px] text-sm text-neutral-400 overflow-hidden duration-200'>{getTranslationString(i18n.language.toUpperCase() as Language, event.m_oEvent, "m_sDescriptionNL") || event.m_oEvent.m_sDescriptionNL}</p>
                      <div className='absolute bottom-0 w-full h-6 bg-gradient-to-t from-white/90 to-transparent'></div>
                    </div>
                    <div className='flex items-center justify-between'>
                      <p className={`${!event.m_oEvent.m_bClosed && 'text-green-700 underline font-medium'} text-sm flex items-center gap-2`}>
                      {event.m_oEvent.m_bClosed ? <Flag className='w-4' strokeWidth={1.5}/> : <Bike className='w-4' strokeWidth={1.5}/> }
                      {event.m_oEvent.m_bClosed ? t("partner.expired") : t("partner.join")}</p>
                    </div>        
                  </div>
                </div>
                </Link>
              )
            })}
            
          </div>
        </div>
      </div>

    <Footer />
    </>
  )
}

export default Department
